import { z } from 'zod'
import { GRUPOS_DA_DRE, gruposDaDreDaConta, type GrupoDaDre } from '@dv/domain'

export const naturezaDeContaContabil = z.enum([
  'receita',
  'despesa',
  'movimentacao',
  'investimento',
  'financeira',
])
export type NaturezaDeContaContabil = z.infer<typeof naturezaDeContaContabil>

export const tipoDeContaContabil = z.enum(['Sintética', 'Analítica'])
export type TipoDeContaContabil = z.infer<typeof tipoDeContaContabil>

/**
 * O Grupo na DRE é lista fechada (DS-545): um dos 17 grupos da cascata, que o domínio
 * guarda. Qual deles a conta aceita depende da natureza e do tipo, e quem confere isso é a
 * API (422 no campo) e o CHECK conta_contabil_grupo_dre_valido (0148).
 */
export const grupoDaDre = z.enum(GRUPOS_DA_DRE, { error: 'Escolha um grupo da lista.' })

/**
 * O nome da linha da DRE em que cada grupo entra, igual ao rótulo da cascata de 23 linhas (D-169),
 * sem o sinal. Nenhum rótulo usa travessão. 'investimento' não é linha: a natureza fica fora da DRE,
 * que recebe só a amortização.
 */
export const ROTULO_DO_GRUPO_DA_DRE: Readonly<Record<GrupoDaDre, string>> = {
  receita_propria: 'Receita própria',
  receita_repasse: 'Receita de repasse',
  deducao: 'Deduções da Receita',
  repasse: 'Custos Diretos · Mídia',
  comissao: 'Custos Diretos · Comissões',
  custos_pj: 'Custos Diretos · Serviços PJ',
  custos_tecnologia: 'Custos Diretos · Tecnologia / Infraestrutura',
  pessoal: 'Despesas Operacionais · Pró-labore e encargos',
  despesas_pj: 'Despesas Operacionais · Serviços PJ',
  administrativas: 'Despesas Operacionais · Administrativas',
  tecnologia: 'Despesas Operacionais · Tecnologia',
  marketing: 'Despesas Operacionais · Marketing',
  viagens: 'Despesas Operacionais · Viagens',
  investimento: 'Investimento (fora da DRE)',
  amortizacao: 'Depreciação e Amortização',
  resultado_financeiro: 'Resultado Financeiro',
  tributos_resultado: 'IRPJ / CSLL',
}

/**
 * Os grupos que a TELA oferece (DS-545): os da natureza, sem 'deducao'. As deduções saem das
 * Alíquotas, uma conta por tributo (as 2.1 a 2.5, achadas pela chave; 0167), e uma conta nova
 * nesse grupo não receberia valor nenhum pelo cálculo e ainda aceitaria classificação que dobraria
 * o tributo. O banco e a API aceitam 'deducao' só porque as cinco contas do sistema o usam.
 */
export function gruposDaDreEscolhiveis(
  natureza: NaturezaDeContaContabil,
  tipo: TipoDeContaContabil,
): readonly GrupoDaDre[] {
  return gruposDaDreDaConta(natureza, tipo).filter((grupo) => grupo !== 'deducao')
}

export const entradaDeContaContabil = z.object({
  codigo: z.string().trim().min(1, 'Informe o código da conta.').max(40),
  nome: z.string().trim().min(1, 'Informe o nome da conta.').max(160),
  paiId: z.uuid().nullable(),
  natureza: naturezaDeContaContabil,
  tipo: tipoDeContaContabil,
  dreGrupo: grupoDaDre.nullable().optional(),
})
export type EntradaDeContaContabil = z.infer<typeof entradaDeContaContabil>

export const edicaoDeContaContabil = entradaDeContaContabil.partial().extend({
  ativa: z.boolean().optional(),
})
export type EdicaoDeContaContabil = z.infer<typeof edicaoDeContaContabil>

export interface ContaContabil {
  readonly id: string
  readonly codigo: string
  readonly nome: string
  readonly paiId: string | null
  readonly natureza: NaturezaDeContaContabil
  readonly tipo: TipoDeContaContabil
  readonly dreGrupo: GrupoDaDre | null
  readonly ativa: boolean
  readonly nivel: number
  readonly uso: number
  /**
   * Conta que o sistema procura pela `chave_sistema` (DS-598, DS-600): código, nome e conta-pai
   * mudam; natureza, tipo, grupo na DRE e situação, não, e ela não se exclui.
   */
  readonly sistema: boolean
  /**
   * DS-629: o tributo da nota que a conta do sistema recebe (`fn_tributo_da_conta`, pela chave), ou
   * nulo. Só leitura e derivado da chave: não é gravável nem uma segunda fonte da identidade. Ausente na API
   * anterior à v1.6.1: a tela lê a falta como "sem função tributária", nunca como erro.
   */
  readonly tributo?: TributoDaContaDoSistema | null
  /**
   * DS-629: os tributos do resultado (IRPJ, CSLL) cujo calendário fiscal aponta para esta conta, em
   * qualquer vigência. Com algum, natureza, tipo, grupo na DRE e situação ficam protegidos, como na
   * conta do sistema, porque o reconhecido e a apuração procuram a conta por esse ponteiro. Ausente na API
   * anterior à v1.6.1 (lida como nenhum).
   */
  readonly calendarioFiscal?: readonly TributoDoCalendarioDaConta[]
}

/** Os tributos da nota que têm conta do sistema, achada pela chave (D-167). */
export type TributoDaContaDoSistema = 'ISS' | 'PIS' | 'COFINS' | 'CBS' | 'IBS'
/** Os tributos cuja conta da DRE é a do calendário fiscal, sem chave (D-165, DS-615). */
export type TributoDoCalendarioDaConta = 'IRPJ' | 'CSLL'

export interface RespostaDoPlanoDeContas {
  readonly data: readonly ContaContabil[]
  readonly meta: {
    readonly total: number
    readonly ativas: number
    readonly sinteticas: number
    readonly analiticas: number
    readonly podeCriar: boolean
    readonly podeEditar: boolean
    readonly podeExcluir: boolean
    readonly podeCriarCatalogo: boolean
  }
}

export interface RespostaDeEdicaoDeContaContabil {
  readonly conta: ContaContabil
  readonly avisoHistorico?: boolean
}
